import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, X, Loader2, Calendar, MapPin, Users, ChevronDown, ChevronUp, Plane, ExternalLink } from "lucide-react";
import { fmtDate } from "../lib/formatters";

/**
 * PendingApprovalsCard — Founder / BDOM priority tile that surfaces every
 * program-booking application and weekend-tour request submitted by a
 * partner but not yet approved/rejected. Each row is one-tap actionable
 * from the dashboard so nobody has to hunt across Partner Applications
 * and Weekend Requests screens to clear the backlog.
 */
export default function PendingApprovalsCard() {
  const [apps, setApps] = useState([]);
  const [weekends, setWeekends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // `${kind}-${id}` while the API call is in flight
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, w] = await Promise.all([
        api.get("/partner-applications").catch(() => ({ data: [] })),
        api.get("/partner-weekend-bookings").catch(() => ({ data: [] })),
      ]);
      setApps((a.data || []).filter(r => (r.status || "").toLowerCase() === "pending"));
      setWeekends((w.data || []).filter(r => (r.status || "").toLowerCase() === "pending"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const total = apps.length + weekends.length;
  const preview = useMemo(() => {
    // Blend program apps + weekend requests into a single time-sorted feed
    // so the newest submission (whatever type) always appears at the top.
    const merged = [
      ...apps.map(a => ({ kind: "program", data: a, ts: a.created_at || "" })),
      ...weekends.map(w => ({ kind: "weekend", data: w, ts: w.created_at || "" })),
    ].sort((x, y) => (y.ts || "").localeCompare(x.ts || ""));
    return expanded ? merged : merged.slice(0, 3);
  }, [apps, weekends, expanded]);

  const approveProgram = async (aid) => {
    setBusyId(`program-${aid}`);
    try {
      await api.post(`/partner-applications/${aid}/approve`);
      toast.success("Application approved · booking created");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
    finally { setBusyId(null); }
  };
  const rejectProgram = async (aid) => {
    const reason = window.prompt("Reason for rejecting? (optional — the partner will see this in their portal)", "");
    if (reason === null) return;
    setBusyId(`program-${aid}`);
    try {
      await api.post(`/partner-applications/${aid}/reject`, { reason: reason.trim() });
      toast.success("Application rejected");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reject"); }
    finally { setBusyId(null); }
  };
  const approveWeekend = async (wid) => {
    setBusyId(`weekend-${wid}`);
    try {
      await api.post(`/partner-weekend-bookings/${wid}/approve`);
      toast.success("Weekend request approved · booking created");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
    finally { setBusyId(null); }
  };
  const rejectWeekend = async (wid) => {
    if (!window.confirm("Reject this weekend request? The partner will be notified.")) return;
    setBusyId(`weekend-${wid}`);
    try {
      await api.post(`/partner-weekend-bookings/${wid}/reject`);
      toast.success("Weekend request rejected");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reject"); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return (
      <div className="aii-card p-5 border-2 border-amber-300 bg-amber-50/50" data-testid="pending-approvals-loading">
        <div className="flex items-center gap-2 text-sm text-amber-900"><Loader2 size={14} className="animate-spin"/> Loading pending approvals…</div>
      </div>
    );
  }
  if (total === 0) {
    return null; // Empty state — hide the card entirely so the dashboard stays clean.
  }

  return (
    <div className="aii-card p-0 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white overflow-hidden" data-testid="pending-approvals-card">
      <div className="p-4 sm:p-5 flex items-start gap-3 border-b border-amber-200">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <AlertTriangle size={20} className="text-amber-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Priority · needs your decision</div>
          <div className="font-display text-lg sm:text-xl font-semibold text-amber-950">
            {total} partner submission{total === 1 ? "" : "s"} waiting for approval
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            {apps.length > 0 && <>{apps.length} program booking{apps.length === 1 ? "" : "s"}</>}
            {apps.length > 0 && weekends.length > 0 && " · "}
            {weekends.length > 0 && <>{weekends.length} weekend tour{weekends.length === 1 ? "" : "s"}</>}
          </div>
        </div>
        {total > 3 && (
          <button onClick={() => setExpanded(v => !v)} data-testid="pending-approvals-toggle"
                  className="text-xs px-2 py-1 border border-amber-300 rounded-md hover:bg-amber-100 font-semibold text-amber-900 flex items-center gap-1 shrink-0">
            {expanded ? <><ChevronUp size={12}/> Show top 3</> : <><ChevronDown size={12}/> Show all {total}</>}
          </button>
        )}
      </div>

      <div className="divide-y divide-amber-100">
        {preview.map(({ kind, data }) => {
          const rowKey = `${kind}-${data.id}`;
          const busy = busyId === rowKey;
          return (
            <div key={rowKey} data-testid={`pending-row-${rowKey}`} className="p-3 sm:p-4 hover:bg-amber-50/60 transition">
              <div className="flex items-start gap-3">
                <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white ${kind === "program" ? "bg-sky-600" : "bg-violet-600"}`}>
                  {kind === "program" ? <Plane size={16}/> : <Calendar size={16}/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className={`text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded ${kind === "program" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>
                      {kind === "program" ? "Program booking" : "Weekend tour"}
                    </span>
                    <span className="text-sm font-semibold truncate max-w-full">
                      {kind === "program" ? (data.name || data.customer || "—") : (data.participant_names || "—")}
                    </span>
                    <span className="text-[11px] text-[color:var(--aii-muted)] truncate">
                      · from <b className="text-[color:var(--aii-text)]">{data.partner_name || "—"}</b>
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-[color:var(--aii-muted)] flex flex-wrap gap-x-3 gap-y-0.5">
                    {kind === "program" ? (
                      <>
                        <span className="inline-flex items-center gap-1"><Users size={10}/> {data.program || "—"}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={10}/> {data.city || data.destination || "—"}</span>
                        <span className="inline-flex items-center gap-1"><Calendar size={10}/> {fmtDate(data.start_date)} → {fmtDate(data.end_date)}</span>
                        {data.duration_weeks && <span>· {data.duration_weeks} wks</span>}
                      </>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1"><MapPin size={10}/> {data.tour_city || data.destinations || "—"}{data.tour_title ? ` · ${data.tour_title}` : ""}</span>
                        <span className="inline-flex items-center gap-1"><Calendar size={10}/> {fmtDate(data.start_dates)} → {fmtDate(data.end_dates)}</span>
                        {data.tour_price ? <span className="font-semibold text-emerald-700">· {data.tour_currency || "USD"} {Number(data.tour_price).toLocaleString()}</span> : <span className="italic">· unpriced</span>}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`pending-approve-${rowKey}`} disabled={busy}
                          onClick={() => kind === "program" ? approveProgram(data.id) : approveWeekend(data.id)}
                          className="px-2.5 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1">
                    {busy ? <Loader2 size={11} className="animate-spin"/> : <Check size={11}/>} Approve
                  </button>
                  <button data-testid={`pending-reject-${rowKey}`} disabled={busy}
                          onClick={() => kind === "program" ? rejectProgram(data.id) : rejectWeekend(data.id)}
                          className="px-2.5 py-1.5 rounded-md border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50 disabled:opacity-50 flex items-center gap-1">
                    <X size={11}/> Reject
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-2.5 border-t border-amber-200 bg-amber-50/70 text-[11px] text-amber-900 flex items-center justify-between">
        <span>Approving creates the booking + sale row automatically. Rejecting notifies the partner in-app + by email.</span>
        <Link to="/partner-admin" data-testid="pending-open-admin" className="ml-3 inline-flex items-center gap-1 font-semibold hover:underline shrink-0">
          Partner admin <ExternalLink size={10}/>
        </Link>
      </div>
    </div>
  );
}
